/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * DatePicker Header - Separate implementation
 * Year and month selectors are separated
 *
 * @author Yakov Gushchin
 * @version 1.10.0
 * @since 1.10.0
 */

import { unitOfTime } from 'moment';

import { icons } from '../../../../../assets';
import { Select } from '../../../index';
import { IOptionItemProps } from '../../../Select/Select';
import useDatePickerHeader from '../../hooks/Header/useDatePickerHeader';
import useDatePickerHeaderSeparate from '../../hooks/Header/useDatePickerHeaderSeparate';
import {
    DatePickerHeaderSeparateCategory,
    DatePickerHeaderTitleSeparateStyled,
} from '../../styled/Header/DatePickerHeaderSeparateStyled';
import {
    DatePickerHeaderLineStyled,
    DatePickerHeaderSelectorArrowStyled,
    DatePickerHeaderSelectorStyled,
    DatePickerHeaderStyled,
    DatePickerHeaderWeekCellStyled,
    DatePickerHeaderWeekStyled,
} from '../../styled/Header/DatePickerHeaderStyled';
import { IDatePickerHeaderProps } from '../../types/datePickerTypes';

type TDatePickerHeaderSeparate = IDatePickerHeaderProps;

const DatePickerHeaderSeparate = (props: TDatePickerHeaderSeparate) => {

    const {
        period,
        locale,
        maxDate,
        minDate,
        periodChangeHandler,
    } = props;

    const selectors: unitOfTime.Base[] = ['month', 'year'];

    /*
    * -------------------------------------------
    *       Hooks: Component logic
    * -------------------------------------------
    */

    const {
        isArrowDisabled,
        onArrowClick,
    } = useDatePickerHeader({
        period,
        maxDate,
        minDate,
        periodChangeHandler,
    });

    const {
        selectedOption,
        selectorChangeHandler,
        selectorOptions,
    } = useDatePickerHeaderSeparate({
        locale,
        maxDate,
        minDate,
        period,
        periodChangeHandler,
    });

    /*
    * -------------------------------------------
    *               Handlers
    * -------------------------------------------
    */

    const onLeftArrowClickHandler = (selectDuration: unitOfTime.Base) => {
        onArrowClick(selectDuration, 'decrease');
    };


    const onRightArrowClickHandler = (selectDuration: unitOfTime.Base) => {
        onArrowClick(selectDuration, 'increase');
    };

    /*
    * -------------------------------------------
    *           Render component
    * -------------------------------------------
    */

    const renderWeekTmpl = <DatePickerHeaderWeekStyled>
        {locale.weekNames.map((week: string, key: number) =>
            // @ts-ignore
            <DatePickerHeaderWeekCellStyled key={key}>{week}</DatePickerHeaderWeekCellStyled>,
        )}
    </DatePickerHeaderWeekStyled>;

    return (
        <DatePickerHeaderStyled>
            <DatePickerHeaderSeparateCategory>
                {
                    selectors.map((selector: unitOfTime.Base) =>
                        <DatePickerHeaderSelectorStyled key={selector}>
                            <DatePickerHeaderSelectorArrowStyled
                                icon={icons.arrowLeftIcon}
                                isDisabled={isArrowDisabled(selector, 'decrease')}
                                side="left"
                                onClick={() => onLeftArrowClickHandler(selector)}
                            />
                            <DatePickerHeaderTitleSeparateStyled>
                                <Select
                                    kind="normal"
                                    options={selectorOptions[selector]}
                                    selectedOption={selectedOption[selector]}
                                    onSelect={(value: IOptionItemProps) => selectorChangeHandler(selector, value)}
                                />
                            </DatePickerHeaderTitleSeparateStyled>
                            <DatePickerHeaderSelectorArrowStyled
                                icon={icons.arrowLeftIcon}
                                isDisabled={isArrowDisabled(selector, 'increase')}
                                side="right"
                                onClick={() => onRightArrowClickHandler(selector)}
                            />
                        </DatePickerHeaderSelectorStyled>,
                    )
                }
            </DatePickerHeaderSeparateCategory>
            {renderWeekTmpl}
            <DatePickerHeaderLineStyled/>
        </DatePickerHeaderStyled>
    );
};

export default DatePickerHeaderSeparate;
